/**
 * GuidedTour Component
 * Lightweight onboarding tour for Hala using driver.js
 * 
 * IMPORTANT: This component is completely isolated and does NOT modify
 * any existing UI elements. It only adds an overlay layer on top.
 */

import { useEffect, useCallback, useState } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

// Storage key for tour completion
const TOUR_COMPLETED_KEY = 'hala_tour_completed';

// Tour steps configuration
const TOUR_STEPS = [
  {
    element: '[data-tour="chat-area"]',
    popover: {
      title: 'مرحباً بك في هلا',
      description: 'هنا تبدأ رحلتك مع هلا. يمكنك كتابة احتياجك أو اختيار ما يناسبك ليتم توجيهك إلى الخدمات المناسبة.',
      side: 'left',
      align: 'center',
    },
  },
  {
    element: '[data-tour="quick-replies"]',
    popover: {
      title: 'فهم احتياجك',
      description: 'ستطرح عليك هلا بعض الأسئلة القصيرة لفهم احتياجك بشكل أفضل وتحديد نوع الدعم المناسب لك.',
      side: 'top',
      align: 'center',
    },
  },
  {
    element: '[data-tour="dashboard"]',
    popover: {
      title: 'الخدمات المقترحة',
      description: 'بعد التحليل ستظهر هنا الخدمات والبرامج المناسبة لك بناءً على احتياجك وموقعك.',
      side: 'right',
      align: 'center',
    },
  },
  {
    element: '[data-tour="service-categories"]',
    popover: {
      title: 'استعرض الخدمات',
      description: 'كل بطاقة تمثل خدمة أو برنامج مناسب لك. يمكنك قراءة التفاصيل واختيار الخدمة التي ترغب بها.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="start-btn"]',
    popover: {
      title: 'سجل اهتمامك',
      description: 'بعد اختيار الخدمة يمكنك تسجيل اهتمامك، وسيتم تحويل طلبك للفريق المختص للتواصل معك.',
      side: 'top',
      align: 'center',
    },
  },
];

// Custom styles for driver.js - scoped to tour only
const injectTourStyles = () => {
  const styleId = 'hala-tour-styles';
  
  // Remove existing if any
  const existing = document.getElementById(styleId);
  if (existing) existing.remove();
  
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    /* Force RTL on all driver.js popovers */
    .driver-popover,
    .driver-popover * {
      unicode-bidi: plaintext !important;
    }
    
    /* Hala Tour Styles - shifted left to avoid overlap */
    .hala-tour-popover {
      font-family: 'Tajawal', 'Segoe UI', sans-serif !important;
      background: white !important;
      border-radius: 16px !important;
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15) !important;
      padding: 0 !important;
      max-width: 340px !important;
      min-width: 300px !important;
      border: none !important;
      direction: rtl !important;
      transform: translateX(-20px) !important;
    }
    
    /* RTL text inside popover - right aligned for Arabic */
    .hala-tour-popover .driver-popover-title {
      font-family: 'Tajawal', 'Segoe UI', sans-serif !important;
      font-size: 18px !important;
      font-weight: 700 !important;
      color: #1B3A6B !important;
      padding: 20px 20px 10px 20px !important;
      margin: 0 !important;
      border: none !important;
      line-height: 1.5 !important;
      direction: rtl !important;
      text-align: right !important;
      unicode-bidi: plaintext !important;
    }
    
    .hala-tour-popover .driver-popover-description {
      font-family: 'Tajawal', 'Segoe UI', sans-serif !important;
      font-size: 14px !important;
      color: #64748B !important;
      line-height: 1.8 !important;
      padding: 0 20px 20px 20px !important;
      margin: 0 !important;
      direction: rtl !important;
      text-align: right !important;
      unicode-bidi: plaintext !important;
    }
    
    .hala-tour-popover .driver-popover-footer {
      display: flex !important;
      flex-direction: row-reverse !important;
      justify-content: space-between !important;
      align-items: center !important;
      padding: 14px 16px !important;
      background: #F8FAFC !important;
      border-top: 1px solid #E2E8F0 !important;
      border-radius: 0 0 16px 16px !important;
      gap: 10px !important;
      direction: rtl !important;
    }
    
    .hala-tour-popover .driver-popover-navigation-btns {
      display: flex !important;
      flex-direction: row-reverse !important;
      gap: 8px !important;
    }
    
    .hala-tour-popover .driver-popover-prev-btn,
    .hala-tour-popover .driver-popover-next-btn {
      font-family: 'Tajawal', 'Segoe UI', sans-serif !important;
      font-size: 13px !important;
      font-weight: 600 !important;
      padding: 8px 20px !important;
      border-radius: 8px !important;
      cursor: pointer !important;
      transition: all 0.2s !important;
      border: none !important;
      text-align: center !important;
    }
    
    .hala-tour-popover .driver-popover-next-btn {
      background: #1D8F4E !important;
      color: white !important;
    }
    
    .hala-tour-popover .driver-popover-next-btn:hover {
      background: #167A42 !important;
    }
    
    .hala-tour-popover .driver-popover-prev-btn {
      background: white !important;
      color: #64748B !important;
      border: 1px solid #E2E8F0 !important;
    }
    
    .hala-tour-popover .driver-popover-prev-btn:hover {
      background: #F1F5F9 !important;
    }
    
    .hala-tour-popover .driver-popover-close-btn {
      position: absolute !important;
      top: 14px !important;
      left: 14px !important;
      right: auto !important;
      width: 28px !important;
      height: 28px !important;
      border-radius: 50% !important;
      background: #F1F5F9 !important;
      border: none !important;
      cursor: pointer !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      font-size: 16px !important;
      color: #64748B !important;
      transition: all 0.2s !important;
    }
    
    .hala-tour-popover .driver-popover-close-btn:hover {
      background: #E2E8F0 !important;
    }
    
    .hala-tour-popover .driver-popover-progress-text {
      font-family: 'Tajawal', 'Segoe UI', sans-serif !important;
      font-size: 13px !important;
      color: #94A3B8 !important;
      direction: ltr !important;
    }
    
    .driver-popover-arrow-side-left {
      border-right-color: white !important;
    }
    
    .driver-popover-arrow-side-right {
      border-left-color: white !important;
    }
    
    .driver-popover-arrow-side-top {
      border-bottom-color: white !important;
    }
    
    .driver-popover-arrow-side-bottom {
      border-top-color: white !important;
    }
    
    /* Overlay */
    .driver-overlay {
      background: rgba(0, 0, 0, 0.5) !important;
    }
    
    /* Highlighted element */
    .driver-active-element {
      z-index: 10000 !important;
    }
  `;
  document.head.appendChild(style);
};

// Remove tour styles
const removeTourStyles = () => {
  const style = document.getElementById('hala-tour-styles');
  if (style) style.remove();
};

/**
 * Hook to manage the guided tour
 */
export function useGuidedTour() {
  const [driverInstance, setDriverInstance] = useState(null);
  
  // Check if tour was completed
  const isTourCompleted = useCallback(() => {
    return localStorage.getItem(TOUR_COMPLETED_KEY) === 'true';
  }, []);
  
  // Mark tour as completed
  const markTourCompleted = useCallback(() => {
    localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
  }, []);
  
  // Reset tour (for manual replay)
  const resetTour = useCallback(() => {
    localStorage.removeItem(TOUR_COMPLETED_KEY);
  }, []);
  
  // Filter steps to only include elements that exist
  const getAvailableSteps = useCallback(() => {
    return TOUR_STEPS.filter(step => {
      const element = document.querySelector(step.element);
      return element !== null;
    });
  }, []);
  
  // Start the tour
  const startTour = useCallback(() => {
    const steps = getAvailableSteps();
    
    if (steps.length === 0) {
      console.log('GuidedTour: No target elements found, skipping tour');
      return;
    }
    
    // Inject styles
    injectTourStyles();
    
    // Function to fix RTL text rendering
    const fixRTLText = () => {
      setTimeout(() => {
        const popover = document.querySelector('.driver-popover');
        const title = document.querySelector('.driver-popover-title');
        const desc = document.querySelector('.driver-popover-description');
        
        if (popover) {
          popover.dir = 'rtl';
        }
        if (title) {
          title.dir = 'rtl';
          title.style.unicodeBidi = 'plaintext';
        }
        if (desc) {
          desc.dir = 'rtl';
          desc.style.unicodeBidi = 'plaintext';
        }
      }, 10);
    };
    
    // Create driver instance
    const driverObj = driver({
      showProgress: true,
      progressText: '{{current}} / {{total}}',
      nextBtnText: 'التالي',
      prevBtnText: 'السابق',
      doneBtnText: 'إنهاء',
      allowClose: true,
      overlayColor: 'rgba(0, 0, 0, 0.5)',
      stagePadding: 8,
      stageRadius: 12,
      popoverOffset: 12,
      animate: true,
      smoothScroll: true,
      popoverClass: 'hala-tour-popover',
      steps: steps,
      onHighlightStarted: fixRTLText,
      onHighlighted: fixRTLText,
      onDestroyStarted: () => {
        markTourCompleted();
        removeTourStyles();
        driverObj.destroy();
      },
      onDestroyed: () => {
        setDriverInstance(null);
      },
    });
    
    setDriverInstance(driverObj);
    driverObj.drive();
    
    // Initial fix
    fixRTLText();
  }, [getAvailableSteps, markTourCompleted]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (driverInstance) {
        driverInstance.destroy();
      }
      removeTourStyles();
    };
  }, [driverInstance]);
  
  return {
    startTour,
    resetTour,
    isTourCompleted,
    closeTour: () => {
      if (driverInstance) {
        driverInstance.destroy();
        setDriverInstance(null);
        removeTourStyles();
      }
    },
  };
}

/**
 * Help Button Component - Tour button for Dashboard
 */
export function TourButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid="help-tour-btn"
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '12px 20px',
        backgroundColor: '#1B3A6B',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontSize: '13px',
        fontWeight: 600,
        fontFamily: "'Tajawal', 'Segoe UI', sans-serif",
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: '0 2px 8px rgba(27, 58, 107, 0.2)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#152d54';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(27, 58, 107, 0.3)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = '#1B3A6B';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(27, 58, 107, 0.2)';
      }}
    >
      <span>جولة هلا الافتراضية</span>
    </button>
  );
}

// Keep HelpButton for backward compatibility but it's not used anymore
export function HelpButton({ onClick }) {
  return null;
}

export default { useGuidedTour, TourButton };
